import type { PapelObraJanelaRequest, PapelObraMarco } from "./papeis-obra.types";

/** O armazenamento é sempre em dias: 1 mês equivale a 30 dias. */
export const DAYS_PER_MONTH = 30;

export type JanelaUnidade = "dias" | "meses";
export type JanelaRelacao = "ANTES" | "DEPOIS";
export type JanelaReferencia = "INICIO" | "TERMINO";

/** Estado de um bloco (Entrada ou Saída) da janela de alocação. */
export interface JanelaBlocoValues {
  /** Entrada: "entra no início da obra"; Saída: "sai no término da obra". */
  marcado: boolean;
  /** Texto digitado; só dígitos. */
  valor: string;
  unidade: JanelaUnidade;
  relacao: JanelaRelacao;
  marco: JanelaReferencia;
}

export interface JanelaValues {
  entrada: JanelaBlocoValues;
  saida: JanelaBlocoValues;
}

export function createInitialJanela(): JanelaValues {
  return {
    entrada: { marcado: true, valor: "", unidade: "dias", relacao: "ANTES", marco: "INICIO" },
    saida: { marcado: true, valor: "", unidade: "dias", relacao: "DEPOIS", marco: "TERMINO" },
  };
}

/** Valor inteiro >= 0 digitado; `null` quando vazio ou inválido. */
export function parseJanelaValor(valor: string): number | null {
  const text = valor.trim();
  if (!/^\d+$/.test(text)) return null;

  return Number.parseInt(text, 10);
}

export function toDays(valor: number, unidade: JanelaUnidade): number {
  return unidade === "meses" ? valor * DAYS_PER_MONTH : valor;
}

export function toMarco(relacao: JanelaRelacao, marco: JanelaReferencia): PapelObraMarco {
  return `${relacao}_${marco}_OBRA` as PapelObraMarco;
}

/** Marco de referência efetivo do bloco: o checkbox fixa início (entrada) ou término (saída). */
function effectiveMarco(bloco: JanelaBlocoValues, fixed: JanelaReferencia): JanelaReferencia {
  return bloco.marcado ? fixed : bloco.marco;
}

/**
 * Posição em dias em relação ao marco: ANTES = -dias, DEPOIS = +dias; bloco
 * marcado = 0. `null` quando o valor ainda não é válido.
 */
function position(bloco: JanelaBlocoValues): number | null {
  if (bloco.marcado) return 0;

  const valor = parseJanelaValor(bloco.valor);
  if (valor === null) return null;

  const dias = toDays(valor, bloco.unidade);
  return bloco.relacao === "ANTES" ? -dias : dias;
}

function pluralDias(dias: number): string {
  return `${dias} ${dias === 1 ? "dia" : "dias"}`;
}

const MARCO_LABEL: Record<JanelaReferencia, string> = {
  INICIO: "início da obra",
  TERMINO: "término da obra",
};

function describeSaida(posicao: number, marco: JanelaReferencia): string {
  if (posicao === 0) return `no ${MARCO_LABEL[marco]}`;

  const relacao = posicao < 0 ? "antes" : "depois";
  return `${pluralDias(Math.abs(posicao))} ${relacao} do ${MARCO_LABEL[marco]}`;
}

export const JANELA_SAME_DAY_MESSAGE =
  "A saída precisa ser depois da entrada. Hoje o papel entra e sai no mesmo dia.";

/**
 * RN06 — com entrada e saída no MESMO marco, a saída precisa ser estritamente
 * depois da entrada. Marcos diferentes dependem da duração da obra e não são
 * validados aqui. Devolve a mensagem de erro ou `null`.
 */
export function validateJanelaOrder(janela: JanelaValues): string | null {
  const marcoEntrada = effectiveMarco(janela.entrada, "INICIO");
  const marcoSaida = effectiveMarco(janela.saida, "TERMINO");
  if (marcoEntrada !== marcoSaida) return null;

  const entrada = position(janela.entrada);
  const saida = position(janela.saida);
  if (entrada === null || saida === null) return null;

  if (saida > entrada) return null;
  if (saida === entrada) return JANELA_SAME_DAY_MESSAGE;

  return `A saída precisa ser depois da entrada. Hoje o papel sai ${describeSaida(saida, marcoSaida)}, ${pluralDias(entrada - saida)} antes de entrar.`;
}

/** Corpo `janela` do contrato; assume os valores já validados. */
export function buildJanelaRequest(janela: JanelaValues): PapelObraJanelaRequest {
  const { entrada, saida } = janela;

  return {
    entrada: entrada.marcado
      ? { inicioDaObra: true }
      : {
          inicioDaObra: false,
          dias: toDays(parseJanelaValor(entrada.valor) ?? 0, entrada.unidade),
          marco: toMarco(entrada.relacao, entrada.marco),
        },
    saida: saida.marcado
      ? { terminoDaObra: true }
      : {
          terminoDaObra: false,
          dias: toDays(parseJanelaValor(saida.valor) ?? 0, saida.unidade),
          marco: toMarco(saida.relacao, saida.marco),
        },
  };
}

/** Valor e unidade para exibir: meses quando múltiplo exato de 30 dias, senão dias. */
function fromDays(dias: number): Pick<JanelaBlocoValues, "valor" | "unidade"> {
  if (dias > 0 && dias % DAYS_PER_MONTH === 0) {
    return { valor: String(dias / DAYS_PER_MONTH), unidade: "meses" };
  }

  return { valor: String(dias), unidade: "dias" };
}

function splitMarco(marco: PapelObraMarco): Pick<JanelaBlocoValues, "relacao" | "marco"> {
  const [relacao, referencia] = marco.split("_");

  return { relacao: relacao as JanelaRelacao, marco: referencia as JanelaReferencia };
}

function blocoFromRequest(
  fallback: JanelaBlocoValues,
  marcado: boolean,
  dias: number | undefined,
  marco: PapelObraMarco | undefined,
): JanelaBlocoValues {
  if (marcado || dias === undefined || !marco) return { ...fallback, marcado: true };

  return { marcado: false, ...fromDays(dias), ...splitMarco(marco) };
}

/** Inversa de `buildJanelaRequest`: janela gravada -> valores do formulário. */
export function janelaRequestToValues(janela: PapelObraJanelaRequest | null): JanelaValues {
  const initial = createInitialJanela();
  if (!janela) return initial;

  return {
    entrada: blocoFromRequest(
      initial.entrada,
      janela.entrada.inicioDaObra,
      janela.entrada.dias,
      janela.entrada.marco,
    ),
    saida: blocoFromRequest(
      initial.saida,
      janela.saida.terminoDaObra,
      janela.saida.dias,
      janela.saida.marco,
    ),
  };
}
